import React from 'react';
import { SprSimListSortKey, SprSimListTabName } from 'types/your-sims';
import {
  ActionsCell,
  ActivationDateCell,
  CheckboxCell,
  ClientCell,
  DeactivationDateCell,
  EsimPDFDownloadCell,
  FailedReasonCell,
  ICCIDCell,
  IMEICell,
  MSISDNCell,
  PausedDateCell,
  PlanIDCell,
  PlanTitleCell,
  PortInStatusCell,
  SimNameCell,
  StatusReasonCell,
  TagsCell,
  UserIdCell,
  ZipCodeCell,
} from './_components/SimListRow/SimListRow';

type Column = {
  key: string;
  headerContent: React.ReactNode;
  sortKey: SprSimListSortKey | null;
  allowedTabs: SprSimListTabName[] | '*';
  CellComponent: React.ComponentType;
};

/**
 * An array of columns config that defines
 * 1. Which columns to be displayed in which tabs
 * 2. What should be the header content for each column
 * 3. What component to render for each column cell
 * 4. Which sorting key to use for each column
 */
export const columns: Column[] = [
  {
    key: 'checkbox',
    headerContent: <>&nbsp;</>,
    sortKey: null,
    allowedTabs: '*',
    CellComponent: CheckboxCell,
  },
  {
    key: 'sim-name',
    headerContent: 'SIM Name',
    sortKey: SprSimListSortKey.SIM_NAME,
    allowedTabs: '*',
    CellComponent: SimNameCell,
  },
  {
    key: 'tags',
    headerContent: 'Tags',
    sortKey: null,
    allowedTabs: '*',
    CellComponent: TagsCell,
  },
  {
    key: 'client',
    headerContent: 'Client',
    sortKey: SprSimListSortKey.CLIENT_NAME,
    allowedTabs: '*',
    CellComponent: ClientCell,
  },
  {
    key: 'zipcode',
    headerContent: 'Zipcode',
    sortKey: SprSimListSortKey.ZIP_CODE,
    allowedTabs: '*',
    CellComponent: ZipCodeCell,
  },
  {
    key: 'imei',
    headerContent: 'IMEI',
    sortKey: null,
    allowedTabs: [SprSimListTabName.AVAILABLE_ESIM],
    CellComponent: IMEICell,
  },
  {
    key: 'user-id',
    headerContent: 'User ID',
    sortKey: null,
    allowedTabs: '*',
    CellComponent: UserIdCell,
  },
  {
    key: 'iccid',
    headerContent: 'ICCID',
    sortKey: SprSimListSortKey.SIM_NAME,
    allowedTabs: '*',
    CellComponent: ICCIDCell,
  },
  {
    key: 'failed-reason',
    headerContent: <>&nbsp;</>,
    sortKey: null,
    allowedTabs: [SprSimListTabName.ACTIVATION_IN_PROGRESS],
    CellComponent: FailedReasonCell,
  },
  {
    key: 'portin-status',
    headerContent: <>&nbsp;</>,
    sortKey: null,
    allowedTabs: [SprSimListTabName.ACTIVATION_IN_PROGRESS],
    CellComponent: PortInStatusCell,
  },
  {
    key: 'msisdn',
    headerContent: 'Phone Number',
    sortKey: SprSimListSortKey.MSISDN,
    allowedTabs: [
      SprSimListTabName.ACTIVE,
      SprSimListTabName.PAUSED,
      SprSimListTabName.DEACTIVE,
    ],
    CellComponent: MSISDNCell,
  },
  {
    key: 'activation-date',
    headerContent: 'Activated Date',
    sortKey: SprSimListSortKey.ACTIVATION_DATE,
    allowedTabs: [
      SprSimListTabName.ACTIVE,
      SprSimListTabName.PAUSED,
      SprSimListTabName.DEACTIVE,
    ],
    CellComponent: ActivationDateCell,
  },
  {
    key: 'plan-title',
    headerContent: 'Rate Plan',
    sortKey: null,
    allowedTabs: [SprSimListTabName.ACTIVE],
    CellComponent: PlanTitleCell,
  },
  {
    key: 'plan-id',
    headerContent: 'Rate Plan ID',
    sortKey: SprSimListSortKey.PLAN_ID,
    allowedTabs: [SprSimListTabName.ACTIVE],
    CellComponent: PlanIDCell,
  },
  {
    key: 'deactivation-date',
    headerContent: 'Deactivated Date',
    sortKey: SprSimListSortKey.DATE_PLAN_CHANGED,
    allowedTabs: [SprSimListTabName.DEACTIVE],
    CellComponent: DeactivationDateCell,
  },
  {
    key: 'paused-date',
    headerContent: 'Paused Date',
    sortKey: SprSimListSortKey.DATE_PLAN_CHANGED,
    allowedTabs: [SprSimListTabName.PAUSED],
    CellComponent: PausedDateCell,
  },
  {
    key: 'status-reason',
    headerContent: 'Deactivation Type',
    sortKey: null,
    allowedTabs: [SprSimListTabName.DEACTIVE],
    CellComponent: StatusReasonCell,
  },
  {
    key: 'esim-pdf-download',
    headerContent: 'eSIM Installation',
    sortKey: null,
    allowedTabs: [SprSimListTabName.ACTIVE],
    CellComponent: EsimPDFDownloadCell,
  },
  {
    key: 'actions',
    headerContent: <>&nbsp;</>,
    sortKey: null,
    allowedTabs: [
      SprSimListTabName.ACTIVATION_IN_PROGRESS,
      SprSimListTabName.AVAILABLE_PSIM,
      SprSimListTabName.AVAILABLE_ESIM,
    ],
    CellComponent: ActionsCell,
  },
];

export const TABS_PATH_NAME_MAP = {
  '/active-sims': SprSimListTabName.ACTIVE,
  '/activation-in-progress': SprSimListTabName.ACTIVATION_IN_PROGRESS,
  '/available-sims': SprSimListTabName.AVAILABLE_PSIM,
  '/available-esims': SprSimListTabName.AVAILABLE_ESIM,
  '/paused-sims': SprSimListTabName.PAUSED,
  '/deactivated-sims': SprSimListTabName.DEACTIVE,
} as const;
